import APIs from '../application/APIs';
import type { SSEConnectionOptions } from '../application/APIs';
import useConfigurationStore from './configurationStore';
import {
  HoldingsListResponse,
  HoldingsUpdateRequest,
  HoldingsUpdateResponse,
  OTCListResponse,
  OTCUpdateRequest,
  OTCSyncResponse,
  OTCComparisonResponse,
  OTCCheckDifferencesResponse,
  BankType,
  PageType,
  AllViewsResponse,
  ViewItem,
  ViewActionResponse,
  ViewsBulkSaveResponse,
  // Authentication types
  LoginRequest,
  LoginResponse,
  UserResponse,
  OrganizationResponse,
  // Service types
  ServiceResponse,
  // Trades types
  TradeUploadResponse,
  // Reports types
  PortfolioInfo,
  AllFamiliesReportResponse,
  ReportType,
  // Portfolio types
  PortfolioUpdateRequest,
  // DataFeed types
  DataFeedRowData,
  DataFeedFileResponse,
  DeleteRowRequest,
  DeleteRowResponse,
  CellUpdateResponse,
  ProcessedFileResponse,
  UploadProcessedRowsResponse,
  // Configuration types
  DanelConfiguration,
  DanelMapping,
  SyncResponse,
  VaultProcessResponse,
  VaultDetectionResponse,
  VaultParseResponse,
  ApplyTemplateRulesResponse,
  GroupItem,
  AllGroupsResponse,
  GroupsBulkSaveResponse,
  GroupPageType,
  // Wizard types
  FileColumnsResponse,
  ColumnMappingRequest,
  SessionDataResponse,
  AccountsResponse,
  CustodiansResponse,
  AccountMappingRequest,
  UploadedFileInfo,
  AccountsMappingStatusResponse,
  IllioOTCDatabaseListResponse,
  IllioOTCDatabaseWithLinksListResponse,
  IllioOTCDatabaseUpdateRequest,
  IllioOTCDatabaseUpdateResponse,
  InstrumentMappingRequest,
  OTCMappingRequest,
  WizardStep,
  InstrumentsMatchResponse,
  OTCMatchResponse,
  OTCInstrumentsResponse,
  PriceFetchItem,
  FetchPricesResponse,
} from '../types/generatedTypes';
import { ConfigurationTemplate } from '../components/DataFeed/Configuration/types';
import { getPortfolioIdForOrganizationTestingHardcoded } from '../config/PortfolioConfig';
import useAuthStore from './authStore';
import { RowType } from '../types/rowType';

const getAllFamiliesReport = async (params?: {
  fromDate?: string;
  toDate?: string;
  portfolios?: PortfolioInfo[];
}): Promise<AllFamiliesReportResponse> => {
  const response = await APIs.getAllFamiliesReport(params);
  return response;
};

const generateReport = async (params: {
  reportType: ReportType;
  portfolios: PortfolioInfo[];
  fromDate?: string;
  toDate?: string;
  isInception: boolean;
  filenameTemplate?: string;
}): Promise<AllFamiliesReportResponse> => {
  const response = await APIs.generateReport(params);
  return response;
};

const uploadDanelExcel = async (file: File) => {
  const response = await APIs.uploadDanelExcel(file);
  return response;
};

const getOTCAssets = async (
  includeInactive: boolean = false
): Promise<OTCListResponse> => {
  const response = await APIs.getOTCAssets(includeInactive);
  return response;
};

const getPortfolioData = async () => {
  const response = await APIs.getPortfolioData();
  return response.portfolio_data;
};

const updateCustodianData = async (
  portfolioId: string,
  updateData: PortfolioUpdateRequest
) => {
  const response = await APIs.updateCustodianData(portfolioId, updateData);
  return response;
};

const updateOTC = async (
  updatedRow: OTCUpdateRequest,
  otcId: string
): Promise<void> => {
  const response = await APIs.updateOTC(updatedRow, otcId);
  return response;
};

const uploadOTCToIllio = async (
  changedRows: OTCUpdateRequest[]
): Promise<void> => {
  const response = await APIs.uploadOTCToIllio(changedRows);
  return response;
};

const syncOTCFromIllio = async (): Promise<OTCSyncResponse> => {
  const response = await APIs.syncOTCFromIllio();
  return response;
};

const getUpdatedOTCComparedToIllio = async (
  otcId: string
): Promise<OTCComparisonResponse> => {
  const response = await APIs.getUpdatedOTCComparedToIllio(otcId);
  return response;
};

const checkAllOTCDifferences =
  async (): Promise<OTCCheckDifferencesResponse> => {
    const response = await APIs.checkAllOTCDifferences();
    return response;
  };

const checkOTCDifferencesRetry = async (otcIds: string[]): Promise<OTCCheckDifferencesResponse> => {
  return await APIs.checkOTCDifferencesRetry(otcIds);
};

const deleteOTCColumn = async (columnName: string): Promise<void> => {
  const response = await APIs.deleteOTCColumn(columnName);
  return response;
};

const downloadOTCBackup = async (): Promise<void> => {
  await APIs.downloadOTCBackup();
};

const restoreOTCFromBackup = async (file: File): Promise<SyncResponse> => {
  const response = await APIs.restoreOTCFromBackup(file);
  return response;
};

// Holdings actions
const getHoldings = async (): Promise<HoldingsListResponse> => {
  const response = await APIs.getHoldings();
  return response;
};

// OTC Database actions
const getOTCDatabase = async (): Promise<IllioOTCDatabaseListResponse> => {
  return await APIs.getOTCDatabase();
};

const getOTCDatabaseWithLinks =
  async (): Promise<IllioOTCDatabaseWithLinksListResponse> => {
    return await APIs.getOTCDatabaseWithLinks();
  };

const syncOTCDatabaseFromIllio = async (): Promise<OTCSyncResponse> => {
  return await APIs.syncOTCDatabaseFromIllio();
};

const updateOTCDatabaseRecord = async (
  otcId: string,
  updatedRow: IllioOTCDatabaseUpdateRequest
): Promise<IllioOTCDatabaseUpdateResponse> => {
  return await APIs.updateOTCDatabaseRecord(otcId, updatedRow);
};

const updateHolding = async (
  holdingId: string,
  updatedRow: HoldingsUpdateRequest
): Promise<HoldingsUpdateResponse> => {
  return await APIs.updateHolding(holdingId, updatedRow);
};

const getDanelData = async (): Promise<DataFeedFileResponse> => {
  const response = await APIs.getDanelData();
  return response;
};

const getProcessedFileData = async (
  fileId: string
): Promise<ProcessedFileResponse> => {
  const response = await APIs.getProcessedFileData(fileId);
  return response;
};

// Danel upload to Illio
const uploadSingleDanelToIllio = async (
  rows: DataFeedRowData,
  appliedTemplateId: string | null
): Promise<TradeUploadResponse> => {
  // Get current organization from auth store
  const organization = useAuthStore.getState().organization;

  if (!organization?.name) {
    throw new Error(
      'No organization context available for portfolio ID selection'
    );
  }

  // Get the appropriate portfolio ID for the organization
  // ! hardcoded!!!!
  const targetPortfolioId = getPortfolioIdForOrganizationTestingHardcoded(
    organization.name
  );

  console.log(
    'Uploading to Illio - Organization:',
    organization?.name,
    'Portfolio ID:',
    targetPortfolioId,
    'Row:',
    rows,
    'Template ID:',
    appliedTemplateId
  );

  const response = await APIs.uploadSingleDanelToIllio(
    rows,
    targetPortfolioId,
    appliedTemplateId
  );
  return response;
};

// Upload processed rows to Illio
const uploadProcessedRows = async (
  processedFileId: string,
  rowIndices: number[]
): Promise<UploadProcessedRowsResponse> => {
  const response = await APIs.uploadProcessedRows(processedFileId, rowIndices);
  return response;
};

const getDanelExcelHeaders = async (): Promise<string[]> => {
  const response = await APIs.getDanelExcelHeaders();
  return response;
};

const saveDanelMappings = async (mappings: DanelMapping[]): Promise<void> => {
  const response = await APIs.saveDanelMappings(mappings);
  return response;
};

const getDanelConfiguration = async (): Promise<DanelConfiguration> => {
  const response = await APIs.getDanelConfiguration();
  return response;
};

const deleteDanelRow = async (rowIndex: number): Promise<DeleteRowResponse> => {
  const deleteRequest: DeleteRowRequest = { row_index: rowIndex };
  const response = await APIs.deleteDanelRow(deleteRequest);
  return response;
};

const updateDanelFileCell = async (
  rowIndex: number,
  columnName: string,
  newValue: string | number | boolean | null
): Promise<CellUpdateResponse> => {
  const response = await APIs.updateDanelFileCell(
    rowIndex,
    columnName,
    newValue
  );
  return response;
};

const resetTableConfiguration = async (tableName: string): Promise<void> => {
  const response = await APIs.resetTableConfiguration(tableName);
  return response;
};

const syncPortfolioCustodianFromIllio = async (): Promise<SyncResponse> => {
  const response = await APIs.syncPortfolioCustodianFromIllio();
  return response;
};

// Views actions - New unified structure with proper typing
const getAllViews = async (): Promise<AllViewsResponse['views']> => {
  const response = await APIs.getAllViews();
  return response || {};
};

const getViewsForPage = async (pageType: PageType): Promise<ViewItem[]> => {
  const response = await APIs.getViewsForPage(pageType);
  return response || [];
};

const saveViewsForPage = async (
  pageType: PageType,
  views: ViewItem[]
): Promise<ViewsBulkSaveResponse> => {
  const response = await APIs.saveViewsForPage(pageType, views);
  return response;
};

const createView = async (
  pageType: PageType,
  view: Omit<ViewItem, 'id'>
): Promise<ViewActionResponse> => {
  const response = await APIs.createView(pageType, view);
  return response;
};

const updateView = async (
  pageType: PageType,
  viewId: string,
  view: Omit<ViewItem, 'id'>
): Promise<ViewActionResponse> => {
  const response = await APIs.updateView(pageType, viewId, view);
  return response;
};

const deleteView = async (
  pageType: PageType,
  viewId: string
): Promise<ViewActionResponse> => {
  const response = await APIs.deleteView(pageType, viewId);
  return response;
};

// Groups API methods
const getAllGroups = async (): Promise<AllGroupsResponse['groups']> => {
  const response = await APIs.getAllGroups();
  return response.groups;
};

const getGroupsForPage = async (
  pageType: GroupPageType
): Promise<GroupItem[]> => {
  const response = await APIs.getGroupsForPage(pageType);
  return response.groups;
};

const saveGroupsForPage = async (
  pageType: GroupPageType,
  groups: GroupItem[]
): Promise<GroupsBulkSaveResponse> => {
  const response = await APIs.saveGroupsForPage(pageType, groups);
  return response;
};

// Authentication actions
const login = async (
  email: string,
  password: string
): Promise<LoginResponse> => {
  const loginRequest: LoginRequest = { email, password };
  const response = await APIs.login(loginRequest);
  return response;
};

const logout = async (): Promise<ServiceResponse> => {
  const response = await APIs.logout();
  return response;
};

const getCurrentUser = async (): Promise<{
  user: UserResponse;
  organization: OrganizationResponse;
}> => {
  const response = await APIs.getCurrentUser();
  return response;
};

const processVaultFiles = async (
  files: { file: File; bank: BankType; name: string }[],
  returnExcel: boolean = false
): Promise<VaultProcessResponse | { message: string }> => {
  const response = await APIs.processVaultFiles(files, returnExcel);
  return response;
};

const getConfigurationTemplate = async (templateId: string) => {
  const response = await APIs.getConfigurationTemplate(templateId);
  return response;
};

const detectVaultTypes = async (
  files: File[]
): Promise<VaultDetectionResponse> => {
  const response = await APIs.detectVaultTypes(files);
  return response;
};

const parseVaultFiles = async (
  files: { file: File; bank: BankType; name: string }[]
): Promise<VaultParseResponse> => {
  const response = await APIs.parseVaultFiles(files);
  return response;
};

const saveParsedVaults = async (
  parsedData: {
    success: boolean;
    filename: string;
    bank_type: string;
    row_count?: number;
    parsed_data: any;
    error?: string | null;
  }[],
  returnExcel: boolean = false,
  rowsType?: RowType
): Promise<VaultProcessResponse | { message: string }> => {
  const response = await APIs.saveParsedVaults(parsedData, returnExcel, rowsType);
  return response;
};

// ========== DataFeed Wizard Actions ==========

// Session Management
const createWizardSession = async (
  fileId: string,
  uploadedFiles?: UploadedFileInfo[],
  name?: string,
  rowsType?: RowType,
  positionsFileId?: string
) => {
  const response = await APIs.createWizardSession(fileId, uploadedFiles, name, rowsType, positionsFileId);
  return response;
};

const checkWizardSessionNameAvailable = async (
  name: string,
  excludeSessionId?: string
) => {
  const response = await APIs.checkWizardSessionNameAvailable(
    name,
    excludeSessionId
  );
  return response;
};

const updateWizardSessionName = async (sessionId: string, name: string) => {
  await APIs.updateWizardSessionName(sessionId, name);
};

const getWizardSession = async (sessionId: string) => {
  const response = await APIs.getWizardSession(sessionId);
  return response;
};

const deleteWizardSession = async (sessionId: string) => {
  const response = await APIs.deleteWizardSession(sessionId);
  return response;
};

// Step 2: Column Mapping
const getWizardFileColumns = async (
  sessionId: string
): Promise<FileColumnsResponse> => {
  const response = await APIs.getWizardFileColumns(sessionId);
  return response;
};

const getWizardFileData = async (
  sessionId: string
): Promise<DataFeedFileResponse> => {
  const response = await APIs.getWizardFileData(sessionId);

  // Update configuration store with column unique values
  if (response.column_unique_values) {
    useConfigurationStore.getState().setColumnUniqueValues(
      response.column_unique_values
    );
  }

  return response;
};

const saveWizardColumnMapping = async (
  sessionId: string,
  config: ColumnMappingRequest
): Promise<SessionDataResponse> => {
  const response = await APIs.saveWizardColumnMapping(sessionId, config);
  return response;
};

// Step 3: Account Linking
const extractWizardAccounts = async (
  sessionId: string
): Promise<{ data: AccountsResponse }> => {
  const data = await APIs.extractWizardAccounts(sessionId);
  return { data };
};

const getWizardCustodians = async (): Promise<{ data: CustodiansResponse }> => {
  const data = await APIs.getWizardCustodians();
  return { data };
};

const saveWizardAccountMappings = async (
  sessionId: string,
  mappings: AccountMappingRequest
): Promise<void> => {
  await APIs.saveWizardAccountMappings(sessionId, mappings);
};

const checkWizardAccountsStatus = async (
  sessionId: string
): Promise<AccountsMappingStatusResponse> => {
  const response = await APIs.checkWizardAccountsStatus(sessionId);
  return response;
};

// Step 4: Instrument Matching
const matchWizardInstruments = async (
  sessionId: string
): Promise<InstrumentsMatchResponse> => {
  const response = await APIs.matchWizardInstruments(sessionId);
  return response;
};

const saveWizardInstrumentMappings = async (
  sessionId: string,
  mappings: InstrumentMappingRequest['mappings']
) => {
  const response = await APIs.saveWizardInstrumentMappings(sessionId, mappings);
  return response;
};

const searchWizardInstruments = async (query: string, type?: string) => {
  const response = await APIs.searchWizardInstruments(query, type);
  return response;
};

const fetchWizardPrices = async (
  items: PriceFetchItem[]
): Promise<FetchPricesResponse> => {
  const response = await APIs.fetchWizardPrices(items);
  return response;
};

// Step 5: OTC Matching
const matchWizardOTC = async (sessionId: string): Promise<OTCMatchResponse> => {
  const response = await APIs.matchWizardOTC(sessionId);
  return response;
};

const getWizardOTCInstruments = async (): Promise<OTCInstrumentsResponse> => {
  const response = await APIs.getWizardOTCInstruments();
  return response;
};

const saveWizardOTCMappings = async (
  sessionId: string,
  mappings: OTCMappingRequest['mappings']
) => {
  const response = await APIs.saveWizardOTCMappings(sessionId, mappings);
  return response;
};

const moveWizardItem = async (
  sessionId: string,
  itemId: string,
  category: 'instrument' | 'otc'
): Promise<SessionDataResponse> => {
  const response = await APIs.moveWizardItem(sessionId, itemId, category);
  return response;
};

const advanceWizardStep = async (
  sessionId: string,
  targetStep: WizardStep
): Promise<void> => {
  await APIs.advanceWizardStep(sessionId, targetStep);
};

// Step 6: Finalize & Upload
const finalizeWizardUpload = async (sessionId: string) => {
  const response = await APIs.finalizeWizardUpload(sessionId);
  return response;
};

const getWizardUploadStatus = async (sessionId: string) => {
  const response = await APIs.getWizardUploadStatus(sessionId);
  return response;
};

const getWizardStepStatus = async (sessionId: string) => {
  const response = await APIs.getWizardStepStatus(sessionId);
  return response;
};

const updateProcessedCell = async (
  sessionId: string,
  rowIndex: number,
  columnName: string,
  newValue: string | number | boolean | null,
  fileId: string
) => {
  const response = await APIs.updateProcessedCell(
    sessionId,
    rowIndex,
    columnName,
    newValue,
    fileId
  );
  return response;
};

const listWizardSessions = async (includeComplete: boolean = true) => {
  const response = await APIs.listWizardSessions(includeComplete);
  return response;
};

const compareWizardPositions = async (sessionId: string) => {
  const response = await APIs.compareWizardPositions(sessionId);
  return response;
};

const completeWizardSession = async (sessionId: string): Promise<void> => {
  await APIs.completeWizardSession(sessionId);
};

// ========== Configuration Template Actions ==========

const saveConfigurationTemplate = async (
  template: ConfigurationTemplate
): Promise<void> => {
  const response = await APIs.saveConfigurationTemplate(template);
  return response;
};

const listConfigurationTemplates = async (): Promise<
  ConfigurationTemplate[]
> => {
  const response = await APIs.listConfigurationTemplates();
  return response;
};

const deleteConfigurationTemplate = async (
  templateId: string
): Promise<void> => {
  const response = await APIs.deleteConfigurationTemplate(templateId);
  return response;
};

const renameConfigurationTemplate = async (
  templateId: string,
  newName: string
): Promise<void> => {
  const response = await APIs.renameConfigurationTemplate(templateId, newName);
  return response;
};

const applyTemplateRules = async (
  templateId?: string | null,
  sessionId?: string | null,
  preserveEditedValues?: boolean
): Promise<ApplyTemplateRulesResponse> => {
  const response = await APIs.applyTemplateRules(
    templateId,
    sessionId,
    preserveEditedValues
  );
  return response;
};

// SSE Connection - creates a Server-Sent Events connection to the given endpoint
const createSSEConnection = <T>(
  endpoint: string,
  options: SSEConnectionOptions<T>
): AbortController => {
  return APIs.createSSEConnection<T>(endpoint, options);
};

export default {
  // SSE Connection
  createSSEConnection,
  getAllFamiliesReport,
  generateReport,
  uploadDanelExcel,
  getOTCAssets,
  getPortfolioData,
  updateCustodianData,
  updateOTC,
  uploadOTCToIllio,
  syncOTCFromIllio,
  getUpdatedOTCComparedToIllio,
  checkAllOTCDifferences,
  checkOTCDifferencesRetry,
  deleteOTCColumn,
  downloadOTCBackup,
  restoreOTCFromBackup,
  getHoldings,
  getOTCDatabase,
  getOTCDatabaseWithLinks,
  syncOTCDatabaseFromIllio,
  updateOTCDatabaseRecord,
  updateHolding,
  getDanelData,
  getProcessedFileData,
  uploadSingleDanelToIllio,
  uploadProcessedRows,
  getDanelExcelHeaders,
  saveDanelMappings,
  getDanelConfiguration,
  deleteDanelRow,
  updateDanelFileCell,
  resetTableConfiguration,
  syncPortfolioCustodianFromIllio,
  processVaultFiles,
  detectVaultTypes,
  parseVaultFiles,
  saveParsedVaults,
  // Views - New unified structure
  getAllViews,
  getViewsForPage,
  saveViewsForPage,
  createView,
  updateView,
  deleteView,
  // Groups
  getAllGroups,
  getGroupsForPage,
  saveGroupsForPage,
  // Authentication
  login,
  logout,
  getCurrentUser,
  // Configuration Template
  getConfigurationTemplate,
  // DataFeed Wizard
  createWizardSession,
  checkWizardSessionNameAvailable,
  updateWizardSessionName,
  getWizardSession,
  deleteWizardSession,
  listWizardSessions,
  getWizardFileColumns,
  getWizardFileData,
  saveWizardColumnMapping,
  extractWizardAccounts,
  getWizardCustodians,
  saveWizardAccountMappings,
  checkWizardAccountsStatus,
  matchWizardInstruments,
  saveWizardInstrumentMappings,
  searchWizardInstruments,
  fetchWizardPrices,
  matchWizardOTC,
  getWizardOTCInstruments,
  saveWizardOTCMappings,
  moveWizardItem,
  advanceWizardStep,
  finalizeWizardUpload,
  getWizardUploadStatus,
  getWizardStepStatus,
  compareWizardPositions,
  completeWizardSession,
  updateProcessedCell,
  // Configuration Templates
  saveConfigurationTemplate,
  listConfigurationTemplates,
  deleteConfigurationTemplate,
  renameConfigurationTemplate,
  applyTemplateRules,
};
